import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { trackEvent } from '../lib/firebase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MobileHeader } from './MobileHeader';
import { Play, TrendingUp, Clock } from 'lucide-react';

interface QuestionSet {
  id: string;
  title: string;
  questionCount?: number;
  description?: string;
  state: 'open' | 'active' | 'closed';
}

interface Poll {
  id: string;
  title: string;
  question: string;
  options: Array<{
    id: string;
    emoji: string;
    label?: string;
  }>;
  state: 'open' | 'active' | 'closed';
  stayActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface GuessTheValue {
  id: string;
  title: string;
  description: string;
  correctAnswer: string;
  caseSensitive: boolean;
  mode: 'multiple' | 'collect';
  state: 'open' | 'active' | 'closed';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

function Lobby() {
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [guessGames, setGuessGames] = useState<GuessTheValue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [joiningRoom, setJoiningRoom] = useState<string | null>(null);

  const { player, isAuthenticated } = usePlayer();
  const { signOut, user } = useAuth();
  const { joinRoom, connected } = useSocket();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Fetch question sets and polls
  useEffect(() => {
    const fetchData = async () => {
      try {
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        
        // Fetch question sets
        const questionSetsResponse = await fetch(`${serverUrl}/api/question-sets`);
        if (questionSetsResponse.ok) {
          const questionSetsData = await questionSetsResponse.json();
          // Filter out closed question sets - only show open and active
          const activeQuestionSets = (questionSetsData.questionSets || []).filter(
            (qs: QuestionSet) => qs.state === 'open' || qs.state === 'active'
          );
          setQuestionSets(activeQuestionSets);
        } else {
          console.warn('Failed to fetch question sets');
          setQuestionSets([]);
        }

        // Fetch polls
        const pollsResponse = await fetch(`${serverUrl}/api/admin/polls`);
        if (pollsResponse.ok) {
          const pollsData = await pollsResponse.json();
          // Filter out closed polls - only show open and active
          const activePolls = (pollsData.polls || []).filter(
            (poll: Poll) => poll.state === 'open' || poll.state === 'active'
          );
          setPolls(activePolls);
          console.log('📊 Fetched active polls:', activePolls);
        } else {
          console.warn('Failed to fetch polls, using mock data');
          // Fallback with mock data
          setPolls([]);
        }

        // Fetch guess games
        const guessGamesResponse = await fetch(`${serverUrl}/api/admin/guess-games`);
        if (guessGamesResponse.ok) {
          const guessGamesData = await guessGamesResponse.json();
          // Filter out closed guess games - only show open and active
          const activeGuessGames = (guessGamesData.guessGames || []).filter(
            (game: GuessTheValue) => game.state === 'open' || game.state === 'active'
          );
          setGuessGames(activeGuessGames);
          console.log('🎯 Fetched active guess games:', activeGuessGames);
        } else {
          console.warn('Failed to fetch guess games, using mock data');
          // Fallback with mock data
          setGuessGames([]);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        
        // No fallback data - all empty
        setQuestionSets([]);
        setPolls([]);
        setGuessGames([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleJoinRoom = async (questionSetId: string) => {
    if (!player || !connected) {
      setError('Unable to join room. Please check your connection.');
      return;
    }

    setJoiningRoom(questionSetId);
    setError('');

    try {
      // Track room join attempt
      trackEvent('room_join_attempt', {
        question_set_id: questionSetId,
        player_nickname: player.nickname
      });

      // Join the room using the question set ID as room ID
      joinRoom(questionSetId, player.nickname, player.id);
      
      // Navigate to game room
      navigate(`/game/${questionSetId}`);
    } catch (err) {
      console.error('Error joining room:', err);
      setError('Failed to join room. Please try again.');
      
      // Track join failure
      trackEvent('room_join_failed', {
        question_set_id: questionSetId,
        error: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setJoiningRoom(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // Navigation will happen automatically when user is signed out
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading question sets...</p>
        </div>
      </div>
    );
  }

  // Convert question sets to trivia games format for the new UI
  const featuredTrivia = questionSets.map((questionSet) => ({
    id: questionSet.id,
    title: questionSet.title,
    description: questionSet.description || 'Test your knowledge',
    state: questionSet.state,
  }));

  // Convert polls to featured polls format for the UI
  const featuredPolls = polls.map((poll) => ({
    id: poll.id,
    title: poll.title,
    description: poll.question,
    options: poll.options.map(option => `${option.emoji} ${option.label || ''}`).slice(0, 4), // Show max 4 options
    state: poll.state,
  }));

  const handleJoinGame = (gameId: string, gameState?: string) => {
    // If game is closed, navigate to results instead of joining
    if (gameState === 'closed') {
      navigate(`/leaderboard/${gameId}`);
      return;
    }
    handleJoinRoom(gameId);
  };

  const handleVoteInPoll = (pollId: string, pollState?: string) => {
    // If poll is closed, navigate to results instead of voting
    if (pollState === 'closed') {
      navigate(`/poll/${pollId}`); // Poll ID already has poll_ prefix
      return;
    }
    // Use existing poll logic - poll ID already has poll_ prefix
    navigate(`/poll/${pollId}`);
  };

  const handleJoinGuessGame = (guessGameId: string, gameState?: string) => {
    // If game is closed, navigate to results instead of joining
    if (gameState === 'closed') {
      navigate(`/guess/${guessGameId}`);
      return;
    }
    navigate(`/guess/${guessGameId}`);
  };

  // Convert guess games to featured guess games format for the UI
  const featuredGuessGames = guessGames.map((game) => ({
    id: game.id,
    title: game.title,
    description: game.description,
    mode: game.mode === 'multiple' ? 'Multiple Attempts' : 'One Attempt',
    caseSensitive: game.caseSensitive,
    state: game.state,
  }));

  return (
    <div className="min-h-screen bg-background">
      <MobileHeader
        isAuthenticated={isAuthenticated}
        user={player ? { name: player.nickname, email: user?.email || '' } : undefined}
        onSignInClick={handleLogout}
        onSignOut={handleLogout}
      />

      <main className="p-4 space-y-6">
        {/* Welcome Section */}
        <div className="text-center py-6">
          <h2 className="font-sans font-semibold text-2xl mb-2">Keep Moving Forward</h2>
          <p className="text-muted-foreground font-mono">
            {isAuthenticated
              ? `Welcome back, ${player?.nickname}! Ready for some trivia?`
              : "Join real-time trivia games and polls with the community"}
          </p>
        </div>

        {/* Connection Status */}
        <div className="flex justify-center mb-4">
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            connected 
              ? 'bg-green-100 text-green-800' 
              : 'bg-destructive/10 text-destructive'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              connected ? 'bg-green-500' : 'bg-destructive'
            }`}></div>
            {connected ? 'Connected' : 'Connecting...'}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="mb-6 border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex">
                <div className="text-destructive">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-destructive">Error</h3>
                  <p className="text-sm text-destructive/80 mt-1">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Live Trivia Games */}
        <section>
          <div className="flex items-center mb-4">
            <h3 className="font-sans font-semibold text-xl">Live Trivia</h3>
          </div>

          {featuredTrivia.length > 0 ? (
            <div className="space-y-3">
              {featuredTrivia.map((game) => (
                <Card key={game.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="font-sans font-semibold text-lg">{game.title}</CardTitle>
                          {game.state === 'active' && (
                            <Badge variant="destructive" className="text-xs">
                              LIVE
                            </Badge>
                          )}
                          {game.state === 'open' && (
                            <Badge variant="secondary" className="text-xs">
                              OPEN
                            </Badge>
                          )}
                          {game.state === 'closed' && (
                            <Badge variant="outline" className="text-xs">
                              CLOSED
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="font-mono">{game.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Button 
                      className="w-full" 
                      size="lg" 
                      onClick={() => handleJoinGame(game.id, game.state)}
                      disabled={!connected || joiningRoom === game.id}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {joiningRoom === game.id ? "Joining..." : 
                       game.state === 'active' ? "Join Game" : 
                       game.state === 'open' ? "Get Ready" : "Closed - View Results"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="mb-6">
              <CardContent className="text-center py-8">
                <div className="text-muted-foreground mb-4">
                  <Play className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No live trivia games</h3>
                <p className="text-muted-foreground mb-4">
                  There are no active trivia games right now. Check back soon or explore past games!
                </p>
                <Button variant="outline" onClick={() => navigate('/past-games')}>
                  View Past Games
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Active Polls */}
        <section>
          <div className="flex items-center mb-4">
            <h3 className="font-sans font-semibold text-xl">Active Polls</h3>
          </div>

          {featuredPolls.length > 0 ? (
            <div className="space-y-3">
              {featuredPolls.map((poll) => (
                <Card key={poll.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="font-sans font-semibold text-lg">{poll.title}</CardTitle>
                          {poll.state === 'active' && (
                            <Badge variant="default" className="text-xs bg-primary">
                              ACTIVE
                            </Badge>
                          )}
                          {poll.state === 'closed' && (
                            <Badge variant="outline" className="text-xs">
                              CLOSED
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="font-mono">{poll.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {poll.options.map((option, index) => (
                        <div key={index} className="border rounded-md p-3 text-center bg-muted/30 text-muted-foreground">
                          <span className="text-base">{option}</span>
                        </div>
                      ))}
                    </div>
                    <Button className="w-full" size="lg" onClick={() => handleVoteInPoll(poll.id, poll.state)}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      {poll.state === 'closed' ? 'Closed - View Results' : 'Enter Poll'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="mb-6">
              <CardContent className="text-center py-8">
                <div className="text-muted-foreground mb-4">
                  <TrendingUp className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No active polls</h3>
                <p className="text-muted-foreground mb-4">
                  There are no active polls right now. Check back soon or explore past polls!
                </p>
                <Button variant="outline" onClick={() => navigate('/past-games')}>
                  View Past Games
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Guess the Value Games */}
        <section>
          <div className="flex items-center mb-4">
            <h3 className="font-sans font-semibold text-xl">Guess the Value</h3>
          </div>

          {featuredGuessGames.length > 0 ? (
            <div className="space-y-3">
              {featuredGuessGames.map((game) => (
                <Card key={game.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="font-sans font-semibold text-lg">{game.title}</CardTitle>
                          {game.state === 'active' && (
                            <Badge variant="default" className="text-xs bg-primary">
                              ACTIVE
                            </Badge>
                          )}
                          {game.state === 'open' && (
                            <Badge variant="secondary" className="text-xs">
                              OPEN
                            </Badge>
                          )}
                          {game.state === 'closed' && (
                            <Badge variant="outline" className="text-xs">
                              CLOSED
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="font-mono">{game.description}</CardDescription>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <Badge variant="outline" className="text-xs">
                        {game.mode}
                      </Badge>
                      {game.caseSensitive && (
                        <Badge variant="outline" className="text-xs">
                          Case Sensitive
                        </Badge>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Button 
                      className="w-full" 
                      size="lg" 
                      onClick={() => handleJoinGuessGame(game.id, game.state)}
                      disabled={!connected}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {game.state === 'closed' ? 'Closed - View Results' : 'Join Game'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="mb-6">
              <CardContent className="text-center py-8">
                <div className="text-muted-foreground mb-4">
                  <Play className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No active guess games</h3>
                <p className="text-muted-foreground mb-4">
                  There are no active guess games right now. Check back soon!
                </p>
                <Button variant="outline" onClick={() => navigate('/past-games')}>
                  View Past Games
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Overall empty state - only show when trivia, polls, and guess games are empty */}
        {featuredTrivia.length === 0 && featuredPolls.length === 0 && featuredGuessGames.length === 0 && !isLoading && (
          <Card className="mt-6">
            <CardContent className="text-center py-12">
              <div className="text-muted-foreground mb-4">
                <div className="flex justify-center gap-4">
                  <Play className="w-12 h-12" />
                  <TrendingUp className="w-12 h-12" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Welcome to X-Team Trivia!</h3>
              <p className="text-muted-foreground mb-4">
                This app features live trivia games and interactive polls. There's nothing active right now, but you can explore what's been happening recently.
              </p>
              <Button onClick={() => navigate('/past-games')}>
                <Clock className="h-4 w-4 mr-2" />
                Explore Past Games
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export default Lobby; 